import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { usePaginatedQuery, useAction } from 'convex/react';
import { api } from '@/lib/api';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from '@/template/lib/toast';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/ui/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable } from '@/components/ui/data-table';
import {
  buildRatgeberColumns,
  RATGEBER_CATEGORIES,
  RATGEBER_STATUSES,
  type RatgeberCategory,
  type RatgeberStatus,
} from '@/components/features/operator/blog/ratgeber-columns';

const EmptyState = ({ t }: { t: (key: string) => string }): React.JSX.Element => (
  <div className='py-16 text-center'>
    <Icons.Inbox className='size-12 text-muted-foreground mx-auto mb-3' />
    <p className='text-muted-foreground text-sm'>{t('blog.empty.title')}</p>
    <p className='text-muted-foreground text-xs mt-1'>{t('blog.empty.description')}</p>
  </div>
);

const OperatorBlogPage = () => {
  const { t, i18n } = useTranslation('operator');
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<RatgeberStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<RatgeberCategory | 'all'>('all');
  const [isRebuilding, setIsRebuilding] = useState(false);

  const triggerRebuild = useAction(api.app.ratgeberActions.triggerMarketingRebuild);

  const handleRebuild = useCallback(async () => {
    setIsRebuilding(true);
    try {
      const result = await triggerRebuild({});
      if (result.status === 'dispatched') {
        toast.success(t('blog.rebuild.dispatched'), { description: result.message });
      } else if (result.status === 'cooldown') {
        toast.warning(t('blog.rebuild.cooldown'), { description: result.message });
      } else {
        toast.error(t('blog.rebuild.error'), { description: result.message });
      }
    } catch {
      toast.error(t('blog.rebuild.error'));
    } finally {
      setIsRebuilding(false);
    }
  }, [triggerRebuild, t]);

  const queryArgs = useMemo<{ status?: RatgeberStatus; category?: RatgeberCategory }>(() => {
    const args: { status?: RatgeberStatus; category?: RatgeberCategory } = {};
    if (statusFilter !== 'all') args.status = statusFilter;
    if (categoryFilter !== 'all') args.category = categoryFilter;
    return args;
  }, [statusFilter, categoryFilter]);

  const { results, status, loadMore } = usePaginatedQuery(
    api.app.ratgeberQueries.listRatgeberPosts,
    queryArgs,
    { initialNumItems: 50 }
  );

  const formatDate = useCallback(
    (timestamp: number): string => {
      const date = new Date(timestamp);
      return i18n.language.startsWith('en') ? format(date, 'P') : format(date, 'P', { locale: de });
    },
    [i18n.language]
  );

  const columns = useMemo(() => buildRatgeberColumns({ t, formatDate }), [t, formatDate]);

  const isLoading = status === 'LoadingFirstPage';

  return (
    <div className='container mx-auto py-8 space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>{t('blog.title')}</h1>
          <p className='text-muted-foreground mt-1'>{t('blog.subtitle')}</p>
        </div>
        <div className='flex items-center gap-2'>
          <Button variant='outline' onClick={handleRebuild} disabled={isRebuilding}>
            <Icons.RefreshCw className={`size-4 mr-2${isRebuilding ? ' animate-spin' : ''}`} />
            {t('blog.actions.rebuild')}
          </Button>
          <Button onClick={() => navigate({ to: '/operators/blog/$postId', params: { postId: 'new' } })}>
            <Icons.Plus className='size-4 mr-2' />
            {t('blog.actions.create')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className='text-base'>{t('blog.filters.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <Select value={statusFilter} onValueChange={(value: string | null) => setStatusFilter((value as RatgeberStatus | 'all') ?? 'all')}>
              <SelectTrigger>
                <SelectValue placeholder={t('blog.filters.statusPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>{t('blog.filters.status.all')}</SelectItem>
                {RATGEBER_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`blog.status.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={categoryFilter} onValueChange={(value: string | null) => setCategoryFilter((value as RatgeberCategory | 'all') ?? 'all')}>
              <SelectTrigger>
                <SelectValue placeholder={t('blog.filters.categoryPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>{t('blog.filters.categories.all')}</SelectItem>
                {RATGEBER_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {t(`blog.categories.${c}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle>{t('blog.table.title')}</CardTitle>
            {!isLoading && results.length > 0 && <Badge variant='secondary'>{t('blog.table.results', { count: results.length })}</Badge>}
          </div>
        </CardHeader>
        <CardContent className='p-4'>
          <div className='max-h-[600px] overflow-y-auto'>
            <DataTable
              columns={columns}
              data={results}
              isLoading={isLoading}
              loadingRowCount={8}
              emptyState={<EmptyState t={t} />}
              getRowId={(post) => post._id}
              onRowClick={(row) => navigate({ to: '/operators/blog/$postId', params: { postId: row.original._id } })}
              headerClassName='sticky top-0 z-10 bg-background'
            />
          </div>
          {status === 'CanLoadMore' && (
            <div className='flex justify-center py-4'>
              <Button variant='outline' onClick={() => loadMore(50)}>
                <Icons.ChevronDown className='size-4 mr-2' />
                {t('blog.actions.loadMore')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/blog/')({
  component: OperatorBlogPage,
});
